import { CheckCircleIcon } from '@c/icons';
import AddBikeModal from '@c/modals/AddGarageModal';
import { XCircleIcon } from '@heroicons/react/20/solid';
import { slugify } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { ProductDocument } from 'models/product';
import { Bike } from 'models/shared';
import { UserDocument } from 'models/user';
import Link from 'next/link';
import { useRouter } from '@bprogress/next';
import { useState } from 'react';
import { FaPencilAlt } from 'react-icons/fa';

type BikePill = Pick<Bike, 'make' | 'model'> & { year: string };

const BikeFitment = ({
  fitment,
  product,
}: {
  fitment: Map<
    string,
    Map<
      string,
      {
        years: number[];
        rideType: string;
      }
    >
  >;
  product: Pick<ProductDocument, 'fitting_bikes'>;
}) => {
  const { userDoc } = useAuth();

  const sortedBikes = userDoc?.bikes.sort((a, b) => {
    const foundBikeA = !!fitment
      .get(a.make)
      ?.get(a.model ?? '')
      ?.years.find((y) => y === a.year);
    const foundBikeB = !!fitment
      .get(b.make)
      ?.get(b.model ?? '')
      ?.years.find((y) => y === b.year);
    if (foundBikeA && !foundBikeB) return -1;
    if (!foundBikeA && foundBikeB) return 1;
    if (foundBikeA && foundBikeB) return 0;
    return 0;
  });

  const hideFitmentInfo = !Boolean(fitment) || product.fitting_bikes?.universal;
  return (
    <div className="flex flex-col gap-[1.6rem] p-[0.8rem] sm:w-full sm:p-0">
      {(!userDoc || !userDoc.bikes.length) && (
        <EmptyGarageCTA userDoc={userDoc} />
      )}

      {hideFitmentInfo && (
        <div className="my-6 flex w-full items-center gap-4 rounded-2xl bg-brand-lightest-gray p-[1.6rem]">
          <InformationCircleIcon className="h-10 w-10" />

          <span className="text-[1.6rem] font-medium text-[#444444]">
            This part has been listed as{' '}
            <span className="font-semibold text-black">universal fitment</span>.
            Please verify compatibility with the seller before purchasing.
          </span>
        </div>
      )}

      {userDoc && userDoc.bikes && !!userDoc.bikes.length && (
        <div className="flex flex-col gap-4">
          <span className="flex items-center gap-4 text-[2.1rem] font-semibold">
            Your Garage
            <Link href="/dashboard/my-garage" aria-label="Edit Your Garage">
              <FaPencilAlt width={24} height={24} />
            </Link>
          </span>

          <div className="flex w-[45rem] gap-8 overflow-x-scroll py-4 sm:w-[90rem]">
            {sortedBikes?.map((bike) => {
              const foundBike = !!fitment
                .get(bike.make)
                ?.get(bike.model ?? '')
                ?.years.find((y) => y === bike.year);

              const foundKeys = Array.from(
                fitment.get(bike.make)?.keys() ?? []
              );

              const foundUniversalBikeMake =
                foundKeys.length && foundKeys.every((y) => y === '');

              return (
                <div
                  className={`flex w-auto min-w-[30rem] flex-col justify-between gap-4 rounded-[1.5rem] p-6 sm:min-w-fit ${
                    foundBike
                      ? 'bg-[#1E7A1E1A]'
                      : product.fitting_bikes?.universal
                      ? 'bg-yellow-200'
                      : 'bg-brand-lightest-gray'
                  }`}
                  key={JSON.stringify(bike)}
                >
                  <span
                    className={`break-words ${
                      foundBike
                        ? 'font-semibold'
                        : 'font-semibold text-zinc-700'
                    }`}
                  >
                    {bike.year} {bike.make} {bike.model}
                  </span>

                  {foundBike ? (
                    <span className="flex items-center gap-4 text-zinc-700">
                      <CheckCircleIcon fill="#1E7A1E" width={24} height={24} />
                      Fits your vehicle
                    </span>
                  ) : product.fitting_bikes?.universal ||
                    foundUniversalBikeMake ? (
                    <span className="flex items-center gap-4 text-zinc-700">
                      <CheckCircleIcon fill="#FFD119" width={24} height={24} />
                      May fit your vehicle
                    </span>
                  ) : (
                    <span className="flex items-center gap-4 text-zinc-700">
                      <XCircleIcon fill="#C41719" width={24} height={24} />
                      Does not fit your vehicle
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const BikePills = ({
  fitment,
  slice,
}: {
  fitment: Map<
    string,
    Map<
      string,
      {
        years: number[];
        rideType: string;
      }
    >
  >;
  slice?: {
    start: number;
    end: number;
  };
}) => {
  const makes = Array.from(fitment.keys());
  return (
    <>
      {makes
        .slice(slice?.start ?? 0, slice?.end ?? makes.length)
        .map((make) => {
          const value = fitment.get(make);
          if (!value) return null;
          const models = Array.from(value.keys());
          let rideType = value.get(models[0])?.rideType;
          if (rideType?.endsWith('s')) {
            rideType = rideType.slice(0, -1);
          }
          return (
            <div key={make} className="flex w-full flex-col gap-[1.5rem]">
              <div className="flex w-full border-b border-zinc-200">
                <Link
                  href={`/shop/${slugify(make)}-${slugify(rideType)}-parts`}
                  className="flex h-[6rem] items-center py-6 text-[2.1rem] font-semibold"
                >
                  {make}
                </Link>
              </div>

              <div className="flex w-full flex-wrap gap-4">
                {models.map((model) => {
                  const years = value.get(model)?.years;
                  let rideType = value.get(model)?.rideType;
                  if (rideType?.endsWith('s')) {
                    rideType = rideType.slice(0, -1);
                  }
                  if (!years) return null;
                  return (
                    <Link
                      className="flex flex-wrap gap-[0.8rem] sm:mt-2"
                      key={make + model}
                      href={`/shop/${years[years.length - 1]}-${slugify(
                        make
                      )}-${slugify(model)}-${slugify(rideType)}-parts`}
                    >
                      <BikePill
                        bike={{
                          make,
                          model,
                          year: `${
                            years.length > 1
                              ? `${years[years.length - 1]}-${years[0]}`
                              : years[0]
                              ? years[0]
                              : ''
                          }`,
                        }}
                      />
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
    </>
  );
};

const BikePill = ({
  bike,
}: {
  bike: {
    make: string;
    model?: string;
    year?: string;
  };
}) => {
  return (
    <div
      className={`rounded-full bg-[#F1F1F1] px-6 py-2 text-[1.6rem] text-black transition-all hover:bg-brand-secondary hover:text-brand-white`}
    >
      {bike.year} {bike.make} {bike.model?.replace(',', '')}
    </div>
  );
};

const bikeColors: { [make: string]: string } = {
  beta: 'bg-[#e60000] text-brand-white',
  cobra: 'bg-[#ffe013] text-brand-black',
  'gas-gas': 'bg-[#cc0002] text-brand-white',
  honda: 'bg-[#c80b0b] text-brand-white',
  husqvarna: 'bg-[#143761] text-brand-white',
  kawasaki: 'bg-[#26bf00] text-brand-white',
  ktm: 'bg-[#f27620] text-brand-white',
  sherco: 'bg-[#0806a9] text-brand-white',
  suzuki: 'bg-[#f5f809] text-brand-black',
  yamaha: 'bg-[#003993] text-brand-white',
  'harley-davidson': 'bg-[#f48322] text-brand-black',
  'can-am': 'bg-[#f5f809] text-brand-black',
  ducati: 'bg-[#CB1517] text-brand-black',
};

const InformationCircleIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12 2.625C6.83063 2.625 2.625 6.83063 2.625 12C2.625 17.1694 6.83063 21.375 12 21.375C17.1694 21.375 21.375 17.1694 21.375 12C21.375 6.83063 17.1694 2.625 12 2.625ZM12 6.46875C12.241 6.46875 12.4767 6.54023 12.6771 6.67415C12.8775 6.80806 13.0337 6.99841 13.126 7.2211C13.2182 7.4438 13.2424 7.68885 13.1953 7.92527C13.1483 8.16168 13.0322 8.37884 12.8618 8.54929C12.6913 8.71973 12.4742 8.83581 12.2378 8.88283C12.0014 8.92986 11.7563 8.90572 11.5336 8.81348C11.3109 8.72123 11.1206 8.56502 10.9866 8.3646C10.8527 8.16418 10.7812 7.92855 10.7812 7.6875C10.7812 7.36427 10.9097 7.05427 11.1382 6.82571C11.3668 6.59715 11.6768 6.46875 12 6.46875ZM15 17.0625H9.375V15.5625H11.4375V11.4375H9.9375V9.9375H12.9375V15.5625H15V17.0625Z"
        fill="#C41719"
      />
    </svg>
  );
};

const EmptyGarageCTA = ({
  userDoc,
}: {
  userDoc: UserDocument | null | undefined;
}) => {
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const router = useRouter();

  return (
    <>
      <div className="flex w-full flex-col items-start justify-start gap-y-8 rounded-[2rem] border-2 border-dashed border-zinc-200 p-[2rem] sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-y-2">
          <h3 className="text-[2.1rem] font-bold text-black">
            Your garage is empty
          </h3>

          <span className="text-[1.8rem] text-zinc-600">
            Select your vehicle to make sure this item fits.
          </span>
        </div>

        <button
          type="button"
          className="w-full items-center justify-center rounded-full bg-brand-secondary px-4 py-4 text-center text-[1.8rem] font-semibold text-white transition-all duration-150 hover:bg-brand-secondary-darker sm:w-fit sm:py-2"
          onClick={() => {
            if (userDoc) {
              setShowAddVehicle(true);
            } else {
              router.push('/login');
            }
          }}
        >
          Add your vehicle
        </button>
      </div>

      <AddBikeModal
        isOpen={showAddVehicle}
        dismiss={() => setShowAddVehicle(false)}
      />
    </>
  );
};

export default BikeFitment;
